import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleRounded,
  CancelRounded,
  QuizRounded,
  CloseRounded,
} from '@mui/icons-material';
import {
  generateConversationQuiz,
  getConversationQuiz,
  getConversationQuizEligibility,
  submitConversationQuiz,
} from '../services/api';
import { ConversationQuiz, QuizQuestionResult } from '../types';

type PageState = 'loading' | 'generating' | 'ready' | 'submitting' | 'results' | 'failed' | 'error';

interface QuizPanelProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: number;
}

const QuizPanel: React.FC<QuizPanelProps> = ({ isOpen, onClose, conversationId }) => {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [quiz, setQuiz] = useState<ConversationQuiz | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [results, setResults] = useState<QuizQuestionResult[] | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState<number | null>(null);
  const [totalQuestions, setTotalQuestions] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set on submit when the pass gate (>=70%) marks a picked syllabus topic done.
  const [topicCompleted, setTopicCompleted] = useState<string | null>(null);
  const [passThreshold, setPassThreshold] = useState<number>(70);

  // Cancellable in-flight generate (mirrors QuizPage). If the user closes the
  // panel mid-regen, the previous request is aborted to avoid wasted LLM
  // cost + the React setState-on-unmounted warning.
  const generateAbortRef = useRef<AbortController | null>(null);

  const loadOrGenerate = useCallback(async (regenerate: boolean) => {
    if (Number.isNaN(conversationId)) {
      setError('Invalid conversation id.');
      setPageState('error');
      return;
    }

    if (generateAbortRef.current) {
      generateAbortRef.current.abort();
    }
    const controller = new AbortController();
    generateAbortRef.current = controller;

    setError(null);
    setPageState(regenerate ? 'generating' : 'loading');

    try {
      let data: ConversationQuiz;
      if (regenerate) {
        data = await generateConversationQuiz(conversationId, true, controller.signal);
      } else {
        try {
          const eligibility = await getConversationQuizEligibility(conversationId, controller.signal);
          if (eligibility.has_new_content) {
            setPageState('generating');
            data = await generateConversationQuiz(conversationId, false, controller.signal);
          } else {
            data = await getConversationQuiz(conversationId, controller.signal);
          }
        } catch (err) {
          if (err instanceof Error && err.message === 'QUIZ_NOT_FOUND') {
            setPageState('generating');
            data = await generateConversationQuiz(conversationId, false, controller.signal);
          } else {
            throw err;
          }
        }
      }

      setQuiz(data);
      if (data.status === 'completed' && data.results) {
        setResults(data.results);
        setScore(data.score ?? null);
        setCorrectCount(data.correct_count ?? null);
        setTotalQuestions(data.total_questions ?? null);
        setPageState('results');
      } else if (data.status === 'generating') {
        // A regen is in flight (this tab, or another client). Show the spinner
        // instead of falling through to 'ready' with the prior questions.
        setAnswers({});
        setResults(null);
        setScore(null);
        setCorrectCount(null);
        setTotalQuestions(null);
        setPageState('generating');
      } else if (data.status === 'failed') {
        // Backend marked this row failed after an LLM regen error. Don't try
        // to render an empty quiz — surface a retry card instead. Mirrors
        // QuizPage.tsx's 'failed' branch.
        setAnswers({});
        setResults(null);
        setScore(null);
        setCorrectCount(null);
        setTotalQuestions(null);
        setPageState('failed');
      } else {
        setAnswers({});
        setResults(null);
        setScore(null);
        setCorrectCount(null);
        setTotalQuestions(null);
        setPageState('ready');
      }
    } catch (err: any) {
      // User-cancelled requests aren't errors — swallow silently when a
      // newer request supersedes us or the panel closed mid-flight.
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      // If we just tried to regenerate, the backend row is now status='failed'.
      // Land back on the friendly retry card instead of the generic error
      // screen — same pattern as QuizPage.tsx.
      setPageState(regenerate ? 'failed' : 'error');
    }
  }, [conversationId]);

  // Reload quiz whenever panel opens
  useEffect(() => {
    if (isOpen) {
      loadOrGenerate(false);
    }
  }, [isOpen, loadOrGenerate]);

  // Abort any in-flight request when the panel unmounts.
  useEffect(() => {
    return () => {
      if (generateAbortRef.current) {
        generateAbortRef.current.abort();
      }
    };
  }, []);

  const allAnswered = useMemo(() => {
    if (!quiz?.questions?.length) return false;
    return quiz.questions.every((q) => answers[q.id]);
  }, [quiz, answers]);

  const handleSubmit = async () => {
    if (!allAnswered || pageState !== 'ready') return;
    setPageState('submitting');
    setError(null);
    try {
      const response = await submitConversationQuiz(conversationId, answers);
      setResults(response.results);
      setScore(response.score);
      setCorrectCount(response.correct_count);
      setTotalQuestions(response.total_questions);
      setTopicCompleted(response.topic_completed ?? null);
      if (typeof response.pass_threshold === 'number') setPassThreshold(response.pass_threshold);
      setPageState('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit quiz.');
      setPageState('ready');
    }
  };

  const handleRetake = async () => {
    await loadOrGenerate(true);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className="fixed inset-0 z-40 bg-black/20 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel — fixed overlay on small screens, static side column on large screens
          so it sits beside the chat instead of covering it. */}
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-violet-100 bg-white shadow-2xl sm:w-[420px] lg:static lg:z-auto lg:h-screen-mobile lg:w-[420px] lg:flex-shrink-0 lg:shadow-xl xl:w-[460px]"
        role="dialog"
        aria-label="Quiz panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <QuizRounded className="text-violet-600" fontSize="small" />
            <span className="text-sm font-semibold text-slate-800">
              {quiz?.title || 'Conversation Quiz'}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close quiz panel"
          >
            <CloseRounded fontSize="small" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* Loading / generating */}
          {(pageState === 'loading' || pageState === 'generating') && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <QuizRounded className="mb-3 text-violet-400" fontSize="large" />
              <p className="text-sm text-slate-500">
                {pageState === 'generating'
                  ? 'Building your quiz from this conversation…'
                  : 'Loading quiz…'}
              </p>
            </div>
          )}

          {/* Error */}
          {pageState === 'error' && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Failed — last LLM generation broke. Show a recovery card with a
              clear "Try again" affordance. Same semantics as QuizPage.tsx's
              'failed' branch, but scoped to the side-panel width. */}
          {pageState === 'failed' && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 px-4 py-5 text-center">
              <QuizRounded className="mx-auto mb-2 text-amber-600" fontSize="large" />
              <p className="text-sm font-semibold text-slate-800 mb-1">
                Couldn't build the quiz
              </p>
              <p className="text-xs text-slate-600 mb-4">
                Something went wrong on our end. Try again — usually it works on the next attempt.
              </p>
              <button
                type="button"
                onClick={handleRetake}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Try again
              </button>
            </div>
          )}

          {/* Inline error (non-fatal). NB: skip when pageState is 'failed' —
              the amber recovery card above already states the failure; a red
              banner below it would visually compete and confuse. */}
          {error && pageState !== 'error' && pageState !== 'failed' && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Results score summary */}
          {pageState === 'results' && score !== null && (
            <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3">
              <p className="text-sm text-slate-700">
                You scored{' '}
                <span className="font-semibold text-violet-700">{score}%</span>
                {correctCount != null && totalQuestions != null && (
                  <> ({correctCount}/{totalQuestions} correct)</>
                )}
              </p>
            </div>
          )}

          {/* Topic completed — passed the end-of-topic quiz (>= threshold). */}
          {pageState === 'results' && topicCompleted && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-sm font-semibold text-emerald-800">
                🎉 Topic complete — you passed!
              </p>
              <p className="mt-0.5 text-sm text-emerald-700">
                “{topicCompleted}” is now marked done for you. Nice work!
              </p>
            </div>
          )}

          {/* Scored but below the pass mark — encourage a retake. */}
          {pageState === 'results' && !topicCompleted && score !== null && score < passThreshold && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-800">
                So close! You need {passThreshold}% to mark this topic done — review the answers
                below and give it another go.
              </p>
            </div>
          )}

          {/* Results list */}
          {pageState === 'results' && results && (
            <div className="space-y-3">
              {results.map((result) => (
                <article
                  key={result.question_id}
                  className={`rounded-2xl border p-4 ${
                    result.correct
                      ? 'border-green-200 bg-green-50/50'
                      : 'border-red-200 bg-red-50/50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {result.correct ? (
                      <CheckCircleRounded className="mt-0.5 text-green-600" fontSize="small" />
                    ) : (
                      <CancelRounded className="mt-0.5 text-red-600" fontSize="small" />
                    )}
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{result.question}</p>
                      <p className="mt-1 text-xs text-slate-600">{result.explanation}</p>
                    </div>
                  </div>
                </article>
              ))}

              <button
                type="button"
                onClick={handleRetake}
                className="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Start fresh quiz
              </button>
            </div>
          )}

          {/* Questions */}
          {(pageState === 'ready' || pageState === 'submitting') && quiz?.questions && (
            <div className="space-y-4">
              {quiz.questions.map((question, idx) => (
                <article
                  key={question.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <p className="text-sm font-semibold text-slate-800">
                    {idx + 1}. {question.question}
                  </p>
                  <div className="mt-3 space-y-2">
                    {question.options.map((option) => {
                      const selected = answers[question.id] === option.id;
                      return (
                        <label
                          key={option.id}
                          className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-xs transition-colors ${
                            selected
                              ? 'border-violet-400 bg-violet-50 text-violet-900'
                              : 'border-slate-200 hover:border-violet-200'
                          }`}
                        >
                          <input
                            type="radio"
                            name={question.id}
                            value={option.id}
                            checked={selected}
                            onChange={() =>
                              setAnswers((prev) => ({
                                ...prev,
                                [question.id]: option.id,
                              }))
                            }
                            className="text-violet-600"
                          />
                          <span>{option.text}</span>
                        </label>
                      );
                    })}
                  </div>
                </article>
              ))}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!allAnswered || pageState === 'submitting'}
                className="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pageState === 'submitting' ? 'Submitting…' : 'Submit answers'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuizPanel;
